import { Controller } from "@hotwired/stimulus";

// Stimulus controller for real-time presence status updates.
//
// Listens for `presence:update` CustomEvents dispatched by the
// UserChannel controller and updates the DOM when the other
// participant's status changes.
//
// Also sends a heartbeat every 2 minutes via ConversationChannel
// to keep the current user's web presence alive.
//
// Usage:
//   <div data-controller="presence"
//        data-presence-user-id-value="42"
//        data-presence-locale-value="fr">
//     <!-- status markup rendered server-side -->
//   </div>
export default class extends Controller {
  static values = { userId: Number, locale: String }

  connect() {
    this.boundHandler = this.handlePresence.bind(this);
    document.addEventListener("presence:update", this.boundHandler);
    this.startHeartbeat();
  }

  disconnect() {
    document.removeEventListener("presence:update", this.boundHandler);
    this.stopHeartbeat();
  }

  handlePresence(event) {
    if (event.detail.userId !== this.userIdValue) return;
    this.updateDOM(event.detail.online, event.detail.lastSeenAt);
  }

  updateDOM(online, lastSeenAt) {
    this.element.replaceChildren();

    const p = document.createElement("p");
    if (online) {
      p.className = "text-xs text-green-500 flex items-center gap-1";
      const dot = document.createElement("span");
      dot.className = "w-2 h-2 rounded-full bg-green-500 inline-block";
      p.appendChild(dot);
      p.appendChild(document.createTextNode(
        this.localeValue === "en" ? "Online" : "En ligne"
      ));
    } else if (lastSeenAt) {
      p.className = "text-xs text-gray-400";
      const ago = this.timeAgoInWords(new Date(lastSeenAt));
      p.textContent = this.localeValue === "en" ? `Seen ${ago} ago` : `Vu il y a ${ago}`;
    } else {
      return;
    }
    this.element.appendChild(p);
  }

  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      const sub = window._conversationSubscription;
      if (sub) sub.perform("heartbeat");
    }, 120_000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
  }

  timeAgoInWords(date) {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return this.localeValue === "en" ? "just now" : "quelques secondes";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return this.localeValue === "en" ? `${days}d` : `${days}j`;
  }
}
